/**
 * Where a connection goes.
 *
 * A line between two node *centres* is the obvious first implementation and it is wrong in a way
 * that is obvious the moment you look at a map: it runs through both boxes. A connection starts
 * at the edge of one node and ends at the edge of the other, and the interesting part is what it
 * does in between.
 *
 * The output is a list of path segments rather than an SVG `d` string. That is the same decision
 * the scene model makes and for the same reason: "export re-renders from the scene model, never a
 * screenshot" only holds if the geometry is data. A PDF page and a Canvas 2D context can both
 * draw these; only an SVG renderer can draw a string, and it would be the one renderer that gets
 * the real path while everything else got an approximation.
 */
import { fishboneSlantX } from '@orbit-mind/layout-engine';
function centreOf(rect) {
    return { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
}
/**
 * The side of `rect` facing `towards`, and the point on it.
 *
 * Chosen by comparing the *aspect* of the offset against the aspect of the box, rather than by
 * the raw offset: a wide node with a target slightly above it should still be left or right,
 * because leaving from the top of a 300-pixel-wide node to something almost beside it looks like
 * a mistake.
 */
export function anchorFor(rect, towards) {
    const centre = centreOf(rect);
    const dx = towards.x - centre.x;
    const dy = towards.y - centre.y;
    if (dx === 0 && dy === 0) {
        // Concentric boxes have no meaningful side. Right is arbitrary and stable, which is all a
        // degenerate layout needs.
        return { point: { x: rect.x + rect.width, y: centre.y }, direction: 'right' };
    }
    const halfWidth = rect.width / 2;
    const halfHeight = rect.height / 2;
    // Which side the ray leaves through, decided by comparing the offset's aspect against the
    // box's. Comparing the raw offset instead would send a link out of the top of a 300-pixel-wide
    // node to reach something almost beside it, which reads as a mistake.
    const horizontal = Math.abs(dx) * halfHeight >= Math.abs(dy) * halfWidth;
    if (horizontal) {
        const onRight = dx > 0;
        // Where the ray crosses that side. Clamped, so a near-corner target cannot push the anchor
        // past the end of the edge it is supposed to be on.
        const y = centre.y + (dy * halfWidth) / Math.abs(dx);
        return {
            point: {
                x: onRight ? rect.x + rect.width : rect.x,
                y: Math.min(rect.y + rect.height, Math.max(rect.y, y)),
            },
            direction: onRight ? 'right' : 'left',
        };
    }
    const below = dy > 0;
    const x = centre.x + (dx * halfHeight) / Math.abs(dy);
    return {
        point: {
            x: Math.min(rect.x + rect.width, Math.max(rect.x, x)),
            y: below ? rect.y + rect.height : rect.y,
        },
        direction: below ? 'down' : 'up',
    };
}
/** A unit vector pointing out of a node along `direction`. */
function vectorOf(direction) {
    switch (direction) {
        case 'left':
            return { x: -1, y: 0 };
        case 'right':
            return { x: 1, y: 0 };
        case 'up':
            return { x: 0, y: -1 };
        case 'down':
            return { x: 0, y: 1 };
    }
}
function distance(a, b) {
    return Math.hypot(b.x - a.x, b.y - a.y);
}
/**
 * How far a curve's control point sits from its anchor.
 *
 * Proportional to the span, so a short connection is a gentle bend and a long one is a real
 * curve — a fixed offset makes short links loop absurdly and long ones look almost straight.
 * Bounded at both ends for the same reason.
 */
function curveHandle(from, to) {
    return Math.min(160, Math.max(24, distance(from, to) * 0.4));
}
function offsetBy(point, direction, amount) {
    const vector = vectorOf(direction);
    return { x: point.x + vector.x * amount, y: point.y + vector.y * amount };
}
/**
 * Routes a connection between two node rectangles.
 *
 * `controlPoints` are bend points the user dragged. When present they win outright: the router's
 * opinion is a default, and a person who moved a line meant it.
 */
/**
 * The anchors of a link in a tree that grows vertically: out of the bottom (or top) of the parent
 * and into the top (or bottom) of the child, whatever their aspect.
 *
 * `anchorFor` is right for a link between two free nodes and wrong for a chart: a child far to the
 * side of a wide row would be reached through the parent's *side*, and an org chart whose lines
 * leave the boss sideways reads as a mistake. A tree says which axis it grows along, and the
 * links follow it.
 */
function verticalAnchors(from, to) {
    const below = centreOf(to).y >= centreOf(from).y;
    return {
        start: {
            point: { x: from.x + from.width / 2, y: below ? from.y + from.height : from.y },
            direction: below ? 'down' : 'up',
        },
        end: {
            point: { x: to.x + to.width / 2, y: below ? to.y : to.y + to.height },
            direction: below ? 'up' : 'down',
        },
    };
}
export function route(from, to, routing, controlPoints, 
/** Set by a tree that grows up or down; its links leave and arrive along that axis. */
axis) {
    const vertical = axis === 'vertical' ? verticalAnchors(from, to) : null;
    const start = vertical?.start ?? anchorFor(from, centreOf(to));
    const end = vertical?.end ?? anchorFor(to, centreOf(from));
    if (controlPoints !== undefined && controlPoints.length > 0) {
        return throughPoints(start, end, controlPoints);
    }
    switch (routing) {
        case 'straight':
            return straight(start, end);
        case 'curved':
            return curved(start, end);
        case 'continuous':
            return continuous(start, end, axis === 'vertical');
        case 'orthogonal':
            return orthogonal(start, end);
        case 'elbow':
            return elbow(start, end);
    }
}
function midpointOf(a, b) {
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}
function straight(start, end) {
    return {
        segments: [
            { kind: 'move', to: start.point },
            { kind: 'line', to: end.point },
        ],
        start,
        end,
        midpoint: midpointOf(start.point, end.point),
    };
}
/**
 * A cubic that leaves and arrives along the anchor normals.
 *
 * The default, and what a mind map usually wants: the curve looks attached to both nodes because
 * it is tangent to the side it touches.
 */
function curved(start, end) {
    const handle = curveHandle(start.point, end.point);
    const control1 = offsetBy(start.point, start.direction, handle);
    const control2 = offsetBy(end.point, end.direction, handle);
    return {
        segments: [
            { kind: 'move', to: start.point },
            { kind: 'cubic', control1, control2, to: end.point },
        ],
        start,
        end,
        // The cubic's value at t = 0.5, which is where the curve actually is — not the midpoint of
        // the straight line, which can be a long way off it.
        midpoint: cubicAtHalf(start.point, control1, control2, end.point),
    };
}
/**
 * A curve that leaves horizontally regardless of where the target is.
 *
 * This is the "continuous" family's look: branches flow out sideways and only then turn, so a
 * column of children reads as one stream rather than as spokes.
 */
function continuous(start, end, vertical = false) {
    // Sideways, whatever the angle — branches flow out and only then turn — unless the tree itself
    // grows up or down, when the same flow runs vertically.
    const ahead = vertical
        ? end.point.y >= start.point.y
            ? 'down'
            : 'up'
        : end.point.x >= start.point.x
            ? 'right'
            : 'left';
    const back = ahead === 'down' ? 'up' : ahead === 'up' ? 'down' : ahead === 'right' ? 'left' : 'right';
    const reach = vertical
        ? Math.abs(end.point.y - start.point.y)
        : Math.abs(end.point.x - start.point.x);
    const handle = Math.max(32, reach * 0.5);
    const control1 = offsetBy(start.point, ahead, handle);
    const control2 = offsetBy(end.point, back, handle);
    return {
        segments: [
            { kind: 'move', to: start.point },
            { kind: 'cubic', control1, control2, to: end.point },
        ],
        start,
        end,
        midpoint: cubicAtHalf(start.point, control1, control2, end.point),
    };
}
/** The point on a cubic at t = 0.5, expanded rather than looped for the one t that is needed. */
function cubicAtHalf(p0, p1, p2, p3) {
    return {
        x: (p0.x + 3 * p1.x + 3 * p2.x + p3.x) / 8,
        y: (p0.y + 3 * p1.y + 3 * p2.y + p3.y) / 8,
    };
}
/**
 * Two right angles: out, across, in.
 *
 * The turn happens halfway along the axis the connection leaves on, which keeps parallel links
 * between the same pair of columns from overlapping each other.
 */
function orthogonal(start, end) {
    const leavesHorizontally = start.direction === 'left' || start.direction === 'right';
    const corners = leavesHorizontally
        ? [
            { x: (start.point.x + end.point.x) / 2, y: start.point.y },
            { x: (start.point.x + end.point.x) / 2, y: end.point.y },
        ]
        : [
            { x: start.point.x, y: (start.point.y + end.point.y) / 2 },
            { x: end.point.x, y: (start.point.y + end.point.y) / 2 },
        ];
    return {
        segments: [
            { kind: 'move', to: start.point },
            ...corners.map((corner) => ({ kind: 'line', to: corner })),
            { kind: 'line', to: end.point },
        ],
        start,
        end,
        midpoint: midpointOf(corners[0] ?? start.point, corners[1] ?? end.point),
    };
}
/** One right angle: along the leaving axis, then straight in. */
function elbow(start, end) {
    const leavesHorizontally = start.direction === 'left' || start.direction === 'right';
    const corner = leavesHorizontally
        ? { x: end.point.x, y: start.point.y }
        : { x: start.point.x, y: end.point.y };
    return {
        segments: [
            { kind: 'move', to: start.point },
            { kind: 'line', to: corner },
            { kind: 'line', to: end.point },
        ],
        start,
        end,
        midpoint: corner,
    };
}
/** A polyline through the bend points the user placed. */
function throughPoints(start, end, points) {
    const middle = points[Math.floor(points.length / 2)];
    return {
        segments: [
            { kind: 'move', to: start.point },
            ...points.map((point) => ({ kind: 'line', to: point })),
            { kind: 'line', to: end.point },
        ],
        start,
        end,
        midpoint: middle ?? midpointOf(start.point, end.point),
    };
}
/**
 * The angle, in degrees, a marker at each end should point.
 *
 * Derived from the anchor direction rather than from the path, because a marker is attached to
 * the node it touches — and for a curve the tangent at the endpoint *is* the anchor normal.
 * Measured clockwise from "pointing right", which is what SVG's `rotate` expects.
 */
/**
 * Where a link in a fishbone goes.
 *
 * Not a routing *style* like the others but a diagram, and the three kinds of link in it have
 * nothing in common: a bone joins the head along the spine and then climbs its slanted line; a
 * cause is a short tick off that slanted line; anything deeper hangs off the cause above it like
 * an indented list. The slanted line is `fishboneSlantX`, the same function the layout placed the
 * boxes against, so a tick always meets its line.
 *
 * Every bone's route starts at the head and runs along the spine to its own join, so the spine
 * is drawn by the bones themselves, overlapping, rather than being a line nobody owns.
 */
export function fishboneRoute(from, to, level, spineY) {
    if (level === 'bone') {
        const above = to.y + to.height / 2 < spineY;
        const start = { point: { x: from.x, y: spineY }, direction: 'left' };
        const join = { x: fishboneSlantX(to, spineY, spineY), y: spineY };
        const end = {
            point: { x: to.x + to.width / 2, y: above ? to.y + to.height : to.y },
            direction: above ? 'down' : 'up',
        };
        return {
            segments: [
                { kind: 'move', to: start.point },
                { kind: 'line', to: join },
                { kind: 'line', to: end.point },
            ],
            start,
            end,
            midpoint: join,
        };
    }
    if (level === 'cause') {
        const y = to.y + to.height / 2;
        const start = { point: { x: fishboneSlantX(from, spineY, y), y }, direction: 'left' };
        const end = { point: { x: to.x + to.width, y }, direction: 'right' };
        return {
            segments: [
                { kind: 'move', to: start.point },
                { kind: 'line', to: end.point },
            ],
            start,
            end,
            midpoint: midpointOf(start.point, end.point),
        };
    }
    // An indented list: down (or up) from just inside the parent's left edge, then across.
    const below = to.y > from.y;
    const x = from.x + Math.min(12, from.width / 2);
    const start = {
        point: { x, y: below ? from.y + from.height : from.y },
        direction: below ? 'down' : 'up',
    };
    if (x > to.x && x < to.x + to.width) {
        const end = {
            point: { x, y: below ? to.y : to.y + to.height },
            direction: below ? 'up' : 'down',
        };
        return {
            segments: [
                { kind: 'move', to: start.point },
                { kind: 'line', to: end.point },
            ],
            start,
            end,
            midpoint: midpointOf(start.point, end.point),
        };
    }
    const y = to.y + to.height / 2;
    const entersLeft = x <= to.x;
    const end = {
        point: { x: entersLeft ? to.x : to.x + to.width, y },
        direction: entersLeft ? 'left' : 'right',
    };
    const corner = { x, y };
    return {
        segments: [
            { kind: 'move', to: start.point },
            { kind: 'line', to: corner },
            { kind: 'line', to: end.point },
        ],
        start,
        end,
        midpoint: corner,
    };
}
/**
 * A link in a `simple-` family, where a node is text on a line rather than a box.
 *
 * It leaves from the end of the parent's line and arrives at the start of the child's, so the
 * branch and the underline read as one stroke — the look that makes these families feel like
 * handwriting rather than like boxes and wires. The root is a box even here, and a link out of it
 * leaves from the middle of its side.
 */
export function simpleRoute(from, to, routing, fromIsBox, vertical) {
    let start;
    let end;
    if (vertical) {
        const below = centreOf(to).y >= centreOf(from).y;
        start = {
            point: { x: from.x + from.width / 2, y: below ? from.y + from.height : from.y },
            direction: below ? 'down' : 'up',
        };
        end = {
            point: { x: to.x + to.width / 2, y: below ? to.y : to.y + to.height },
            direction: below ? 'up' : 'down',
        };
    }
    else {
        const right = centreOf(to).x >= centreOf(from).x;
        start = {
            point: {
                x: right ? from.x + from.width : from.x,
                y: fromIsBox ? from.y + from.height / 2 : from.y + from.height,
            },
            direction: right ? 'right' : 'left',
        };
        end = {
            point: { x: right ? to.x : to.x + to.width, y: to.y + to.height },
            direction: right ? 'left' : 'right',
        };
    }
    switch (routing) {
        case 'straight':
            return straight(start, end);
        case 'orthogonal':
            return orthogonal(start, end);
        case 'elbow':
            return elbow(start, end);
        case 'continuous':
            return continuous(start, end, vertical);
        case 'curved':
            return curved(start, end);
    }
}
/**
 * A link in a list family.
 *
 * Two shapes. A row of an outline hangs off its parent: down (or up) from just inside the
 * parent's left edge, then across into the row — `inset` is half the list's indent, so every
 * row of one parent shares that vertical line. A column head, which sits beside the root rather
 * than under it, is reached along a bus: out of the root's side, across, then down into the head.
 *
 * `underlined` ends each link at the bottom of the row, for the `simple-` list whose rows are
 * text on a line; `rounded` softens the corners, for the continuous list.
 */
export function listRoute(from, to, options) {
    const toCentre = centreOf(to);
    const sideBySide = to.x > from.x + from.width || to.x + to.width < from.x;
    let points;
    let startDirection;
    let endDirection;
    if (sideBySide) {
        const right = toCentre.x >= centreOf(from).x;
        const fromY = from.y + from.height / 2;
        const below = to.y >= fromY;
        points = [
            { x: right ? from.x + from.width : from.x, y: fromY },
            { x: toCentre.x, y: fromY },
            { x: toCentre.x, y: below ? to.y : to.y + to.height },
        ];
        startDirection = right ? 'right' : 'left';
        endDirection = below ? 'up' : 'down';
    }
    else {
        const below = toCentre.y >= centreOf(from).y;
        const x = from.x + Math.min(options.inset, from.width / 2);
        const y = options.underlined ? to.y + to.height : toCentre.y;
        points = [
            { x, y: below ? from.y + from.height : from.y },
            { x, y },
            { x: to.x, y },
        ];
        startDirection = below ? 'down' : 'up';
        endDirection = 'left';
    }
    const [first, corner, last] = points;
    const segments = options.rounded
        ? roundedCorner(first, corner, last, 10)
        : [
            { kind: 'move', to: first },
            { kind: 'line', to: corner },
            { kind: 'line', to: last },
        ];
    return {
        segments,
        start: { point: first, direction: startDirection },
        end: { point: last, direction: endDirection },
        midpoint: corner,
    };
}
/** Two straight runs joined by a curve instead of a corner, `radius` either side of it. */
function roundedCorner(first, corner, last, radius) {
    const towards = (from, to, distance) => {
        const length = Math.hypot(to.x - from.x, to.y - from.y);
        if (length === 0)
            return from;
        const t = Math.min(1, distance / length);
        return { x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t };
    };
    const into = towards(corner, first, radius);
    const outOf = towards(corner, last, radius);
    return [
        { kind: 'move', to: first },
        { kind: 'line', to: into },
        { kind: 'cubic', control1: corner, control2: corner, to: outOf },
        { kind: 'line', to: last },
    ];
}
/** The outline of a node that is text on a line: the line under it, and nothing else. */
export function underlineOutline(rect) {
    const y = rect.y + rect.height;
    return [
        { kind: 'move', to: { x: rect.x, y } },
        { kind: 'line', to: { x: rect.x + rect.width, y } },
    ];
}
export function markerAngle(direction) {
    switch (direction) {
        case 'right':
            return 0;
        case 'down':
            return 90;
        case 'left':
            return 180;
        case 'up':
            return 270;
    }
}
/**
 * The segments as an SVG path.
 *
 * Lives here rather than in the renderer so the browser canvas and the SVG exporter cannot
 * disagree about the same route — which is exactly the disagreement that makes an export look
 * subtly unlike what the user approved.
 */
export function toSvgPath(segments) {
    const round = (value) => (Math.round(value * 100) / 100).toString();
    return segments
        .map((segment) => {
        switch (segment.kind) {
            case 'move':
                return `M ${round(segment.to.x)} ${round(segment.to.y)}`;
            case 'line':
                return `L ${round(segment.to.x)} ${round(segment.to.y)}`;
            case 'cubic':
                return `C ${round(segment.control1.x)} ${round(segment.control1.y)} ${round(segment.control2.x)} ${round(segment.control2.y)} ${round(segment.to.x)} ${round(segment.to.y)}`;
        }
    })
        .join(' ');
}
/**
 * The dash pattern for a line type, in map units.
 *
 * Returned rather than named so a Canvas context and an SVG attribute take the same array. `null`
 * for a solid line, because "no dashes" and "dashes of length zero" are different instructions.
 */
export function dashFor(lineType) {
    switch (lineType) {
        case 'solid':
            return null;
        case 'dashed':
            return [6, 4];
        case 'dotted':
            return [1, 3];
    }
}
/**
 * The routing a layout implies for its hierarchy links.
 *
 * The layout id carries the family — `curved-line-tree`, `direct-line-tree`, `cornered-list` —
 * and the connection style is part of what those names mean. Reading it here keeps the two from
 * drifting, which they would if the renderer had its own opinion about what a "cornered" tree
 * looks like.
 */
export function routingForLayout(layoutId) {
    if (layoutId.startsWith('free-form'))
        return 'straight';
    if (layoutId.startsWith('direct-line-tree') || layoutId.includes('simple-tree-direct')) {
        return 'straight';
    }
    if (layoutId.includes('cornered'))
        return 'orthogonal';
    if (layoutId.startsWith('continuous-list'))
        return 'elbow';
    if (layoutId.startsWith('continuous'))
        return 'continuous';
    return 'curved';
}
//# sourceMappingURL=routing.js.map